import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { getPaymentStatus } from "@/lib/api";
import { Logo } from "@/components/Header";

const PaymentSuccess = () => {
  const { t } = useLang();
  const { clear } = useCart();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const res = await getPaymentStatus(sessionId);
        if (res.payment_status === "paid") { setStatus("paid"); clear(); return; }
        if (["expired", "failed"].includes(res.payment_status)) { setStatus("error"); return; }
      } catch { /* retry */ }
      if (attempts.current < 8) timer = setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, clear]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
      <div className="absolute top-6 left-6"><Logo /></div>
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}>
        {status === "checking" ? (
          <div className="flex flex-col items-center gap-4" data-testid="payment-checking">
            <Loader2 className="h-12 w-12 animate-spin text-accent" />
            <p className="text-lg text-muted-foreground">{t.success.checking}</p>
          </div>
        ) : (
          <div className="flex max-w-lg flex-col items-center gap-5" data-testid="payment-success">
            <CheckCircle2 className="h-20 w-20 text-accent" />
            <h1 className="font-heading text-4xl font-bold tracking-tight text-primary">{t.success.title}</h1>
            <p className="text-lg leading-relaxed text-muted-foreground">{t.success.sub}</p>
            <Link to="/" data-testid="success-home-btn"
              className="mt-2 rounded-full bg-primary px-7 py-3.5 font-semibold text-primary-foreground transition-transform hover:bg-accent active:scale-95">
              {t.success.back}
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
